import { useState } from "react";
import type { ReactNode } from "react";
import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import type {
  CandidateStep,
  ConsentPurpose,
  IdType,
  IdVerificationMethod,
  InterviewMode,
  InviteStatus,
} from "@talenthop/shared";
import { api, ApiError } from "../../lib/api.js";
import { Alert, Arrow, Calendar, Check, Info, Clock } from "../../components/icons.js";
import { CandidateStepper, CandidateTopBar } from "./CandidateChrome.js";
import { CandidateInfoStep } from "./CandidateInfoStep.js";
import { ConsentStep } from "./ConsentStep.js";
import { ScheduleStep, SaveLinkCard, VIEWER_TZ, dayLabel, timeLabel } from "./ScheduleStep.js";
import { CheckinStep } from "./CheckinStep.js";
import { InterviewStep } from "./InterviewStep.js";
import { DoneStep } from "./DoneStep.js";
import { RecoverLinkForm } from "./FindMyLink.js";
import { CandidateBackground, type AudioCue } from "./CandidateBackground.js";

/**
 * Candidate journey — /interview/:token (public, capability-token access; no login).
 * Every step is driven by server state (GET /invite/{token} → step). The candidate NEVER
 * sees scores, rankings or a decision here — that surface belongs to the recruiter.
 */

export interface InviteInfo {
  status: InviteStatus;
  candidateName: string | null;
  /** The invite's scoping email — prefills Step 1 and is where the OTP method sends codes. */
  email: string | null;
  /** Optional, candidate-supplied, never required — plain display/storage, no journey effect. */
  linkedinUrl?: string | null;
  orgName: string;
  requisitionTitle: string;
  /** The vertical this role is assessed under. Documented because the invite payload carries it,
   *  deliberately NOT rendered: the candidate journey never previews the rubric it is scored on. */
  verticalKey?: string;
  /** The requisition's proctoring preset (#43). Threaded into ConsentStep (the mode must be
   *  disclosed WHERE consent is asked — DPDP proportionality) and InterviewStep (which enforces
   *  it). Absent — an older API, a cached payload — parses as "natural", the interviewModeOf
   *  default, so a stale client can only under-enforce, never spring strictness unannounced. */
  interviewMode?: InterviewMode;
  step: CandidateStep;
  expiresAt: string;
  consentPurposes: ConsentPurpose[];
  scheduledAt?: string | null;
  preferredEmail?: string | null;
  /** Whether ID verification is on. When false the check-in is a plain device-readiness gate. */
  idvEnabled?: boolean;
  /** Which flow proves identity at check-in when idvEnabled — 'otp' or 'id_document'. */
  idvMethod?: IdVerificationMethod;
  /** Owner-configured accepted government IDs — drives the check-in ID picker. */
  allowedIdTypes?: IdType[];
  /**
   * GENUINE persisted identity outcome for the done screen — true only when the check actually
   * passed AND was NOT simulated (mirrors talent_personas.verified_identity, which inviteCheckin
   * sets only on a real-provider pass). Distinct from `idvEnabled`: the feature can be ON yet the
   * run simulated (pilot 'sim' provider), in which case this stays false so we never tell the
   * candidate "Identity verified" for a demo check. Absent until the API surfaces it → defaults
   * to the honest "recorded" copy.
   */
  identityVerified?: boolean;
  /**
   * Effective recording-retention window (days) the API is actually enforcing via the sweep —
   * derived from recordingRetentionDays() on the invite payload. Threaded into ConsentStep so the
   * "deleted after N days" notice always matches the enforced horizon. Absent until the API
   * surfaces it → ConsentStep falls back to its default.
   */
  recordingRetentionDays?: number;
  /**
   * WS4 (Sep 2026): the languages this requisition offers the candidate — the recruiter's
   * `config.interviewLanguages` intersected with what the owner has enabled AND marked consent-
   * reviewed. `label` is English (for us), `nativeLabel` is the language's own name (what the
   * candidate reads). Absent or a single entry → no picker, English, exactly the pre-WS4 screen;
   * a pre-migration API sends `[en]` for the same reason.
   */
  languages?: InviteLanguage[];
  /** What the candidate chose at consent (candidacies.interview_language). Absent — an older API,
   *  the pre-migration window, or not yet consented — reads as English, the one default. */
  language?: string;
}
/** One offered language, as GET /invite/{token} sends it. `code` is an INTERVIEW_LANGUAGE_CODES
 *  entry today; typed as string so a future API that knows one more language cannot break the
 *  parse — the consent step only renders the ones it recognises. */
export interface InviteLanguage {
  code: string;
  label: string;
  nativeLabel: string;
}
interface InviteResponse {
  invite: InviteInfo;
}

const ORDER: CandidateStep[] = ["invite", "consent", "schedule", "checkin", "interview", "done"];
const STEP_LABEL: Record<CandidateStep, string> = {
  invite: "Invite",
  consent: "Consent",
  schedule: "Schedule",
  checkin: "Check-in",
  interview: "Interview",
  done: "Done",
};

/**
 * Card shown when there is no token, or the link is invalid / expired / revoked. Always
 * carries the recovery form — a dead-ended candidate should never have to chase a human.
 */
function LinkProblem({ title, body }: { title: string; body: string }): ReactNode {
  return (
    <div className="cx-step t1c-problem">
      <span className="t1c-problem-ico" aria-hidden="true">
        <Alert />
      </span>
      <h1 className="t1c-h1">{title}</h1>
      <p className="mut t1c-lede">{body}</p>
      <div className="t1c-problem-form">
        <div className="t1c-problem-next">Get a fresh link</div>
        <RecoverLinkForm />
      </div>
    </div>
  );
}

// Local dev only — never true on a deployed domain, so this can never appear for a real
// candidate. Lets a developer click back through the tracker to re-view an earlier step
// without reseeding a new invite each time.
const IS_LOCAL_TEST =
  typeof window !== "undefined" &&
  (window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1");

export function CandidateApp(): ReactNode {
  const params = useParams<{ token: string }>();
  const token = params.token ?? "";

  // Local-only advance for the two pre-consent screens (server step stays "invite"/"consent"
  // until the consent POST lands); everything after is server-driven and monotonic.
  const [localIdx, setLocalIdx] = useState(0);
  // Booked-state controls: reschedule reopens the slot picker; start-early skips the wait.
  const [rescheduling, setRescheduling] = useState(false);
  const [startEarly, setStartEarly] = useState(false);
  // Dev-only "view an earlier step" override (see IS_LOCAL_TEST). Purely a display jump —
  // it never rewinds the server's real progress, so an action taken on a re-viewed step
  // (e.g. re-submitting consent) still only affects whatever that endpoint actually does;
  // it doesn't erase later steps you already completed.
  const [devViewIdx, setDevViewIdx] = useState<number | null>(null);
  // Published by InterviewStep only (mic/TTS turn-taking) — every other step stays idle.
  const [audioCue, setAudioCue] = useState<AudioCue>({ mode: "idle", level: 0 });

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["invite", token],
    queryFn: () => api.get<InviteResponse>(`/invite/${token}`),
    enabled: token.length > 0,
  });
  const invite = data?.invite ?? null;

  const serverIdx = invite ? ORDER.indexOf(invite.step) : 0;
  const realIdx = Math.max(serverIdx, localIdx);
  const stepIdx = IS_LOCAL_TEST && devViewIdx !== null ? devViewIdx : realIdx;
  const step: CandidateStep = ORDER[Math.min(stepIdx, ORDER.length - 1)] ?? "invite";
  const viewingPast = IS_LOCAL_TEST && devViewIdx !== null && devViewIdx !== realIdx;

  // A finished interview is terminal — never show the "expired" wall for it, even past expiresAt
  // (the candidate should still reach their completion/thank-you state, not a dead link).
  const expired =
    invite !== null &&
    invite.status !== "completed" &&
    (invite.status === "expired" || new Date(invite.expiresAt).getTime() < Date.now());
  const badLink =
    isError && error instanceof ApiError && (error.status === 404 || error.status === 410 || error.status === 403);

  // The live interview goes edge-to-edge (camera + question panel); every other step sits in the
  // boxed card with the brand bar and the one stepper. Capped at 1980px on ultra-wide monitors.
  const fullBleed = invite !== null && !expired && step === "interview";

  if (fullBleed) {
    return (
      <>
        <CandidateBackground mode={audioCue.mode} level={audioCue.level} />
        <div className="cx cx-full t1c-iv-root">
          <InterviewStep
            token={token}
            orgName={invite.orgName}
            requisitionTitle={invite.requisitionTitle}
            onAudioCue={setAudioCue}
            interviewMode={invite.interviewMode}
            language={invite.language}
          />
        </div>
      </>
    );
  }

  // Booked for later, came back early: show the booking with reschedule / start-early instead of
  // shoving them straight into check-in. Computed here so the stepper can say so too.
  const bookedFuture =
    invite !== null &&
    !expired &&
    step === "checkin" &&
    invite.scheduledAt != null &&
    new Date(invite.scheduledAt).getTime() > Date.now() + 10 * 60_000 &&
    !startEarly;

  return (
    <>
      <CandidateBackground mode={audioCue.mode} level={audioCue.level} />
      <div className={`cx t1c${step === "checkin" && !bookedFuture ? " cx-wide" : ""}`}>
      <div className="cx-shell">
        <CandidateTopBar
          label="Verified interview"
          right={
            invite ? (
              <span className="t1c-org" title={invite.orgName}>
                {invite.orgName}
              </span>
            ) : undefined
          }
        />

        {/* The one stepper — same five stages on every step, details page included. */}
        {invite && !expired && (
          <CandidateStepper
            step={bookedFuture && !rescheduling ? "checkin" : bookedFuture ? "schedule" : step}
            note={bookedFuture && !rescheduling ? "booked for later" : undefined}
          />
        )}

        {/* Local-dev-only jump tracker — lets a developer re-view an earlier step for testing.
            Never appears on a deployed domain (IS_LOCAL_TEST). Collapsed so it can't be mistaken
            for the candidate stepper above. */}
        {IS_LOCAL_TEST && invite && !expired && (
          <details className="t1c-devjump">
            <summary>Local test only · jump to a step</summary>
            <div className="t1c-devjump-row">
              {ORDER.map((s, i) => {
                const reached = i <= realIdx;
                return (
                  <button
                    key={s}
                    type="button"
                    className={`btn sm ${i === stepIdx ? "primary" : "ghost"}`}
                    disabled={!reached}
                    data-tip="Local test only — jump to this step"
                    onClick={() => setDevViewIdx(i === realIdx ? null : i)}
                  >
                    {i < stepIdx && <Check />} {STEP_LABEL[s]}
                  </button>
                );
              })}
            </div>
          </details>
        )}

        {viewingPast && (
          <div className="banner warn" style={{ margin: "0 24px 4px" }}>
            <Info />
            <div>
              <b>Local test view</b> — you're re-viewing "{STEP_LABEL[step]}", not your actual current
              step. Actions you take here still hit the real API and won't undo later steps you've
              already completed.
              <button
                type="button"
                className="btn sm ghost"
                style={{ marginLeft: 10 }}
                onClick={() => setDevViewIdx(null)}
              >
                Return to current step
              </button>
            </div>
          </div>
        )}

        {token.length === 0 && (
          <LinkProblem
            title="An interview link is required"
            body="This page opens from a personal invite link we emailed you. Use the link from your invitation — or enter the email it was sent to and we'll send you a fresh one."
          />
        )}

        {token.length > 0 && isLoading && (
          <div className="cx-step">
            <div className="card pad mut sm">Loading your invitation…</div>
          </div>
        )}

        {badLink && (
          <LinkProblem
            title="This link is invalid or has expired"
            body="Interview links are personal and time-limited — nothing has gone wrong with your application. Enter the email your invitation was sent to and we'll send you a fresh link."
          />
        )}
        {isError && !badLink && token.length > 0 && (
          <div className="cx-step">
            <div className="banner crit">
              <Info />
              <span>Couldn't load your invitation. Please retry in a moment.</span>
            </div>
          </div>
        )}

        {invite && expired && (
          <LinkProblem
            title="This invitation has expired"
            body={`Your interview link for ${invite.requisitionTitle} at ${invite.orgName} is no longer active. Enter the email your invitation was sent to and we'll send you a fresh one.`}
          />
        )}

        {invite && !expired && step === "invite" && (
          <CandidateInfoStep
            token={token}
            orgName={invite.orgName}
            requisitionTitle={invite.requisitionTitle}
            candidateName={invite.candidateName}
            email={invite.email}
            linkedinUrl={invite.linkedinUrl ?? null}
            idvEnabled={invite.idvEnabled ?? false}
            idvMethod={invite.idvMethod}
            onContinue={() => setLocalIdx(1)}
          />
        )}
        {invite && !expired && step === "consent" && (
          <ConsentStep
            token={token}
            purposes={invite.consentPurposes}
            retentionDays={invite.recordingRetentionDays}
            interviewMode={invite.interviewMode}
            languages={invite.languages}
          />
        )}
        {invite && !expired && step === "schedule" && (
          <ScheduleStep token={token} savedEmail={invite.preferredEmail ?? null} />
        )}
        {invite && !expired && step === "checkin" && (() => {
          if (bookedFuture && rescheduling) {
            return (
              <ScheduleStep
                token={token}
                savedEmail={invite.preferredEmail ?? null}
                onBack={() => setRescheduling(false)}
              />
            );
          }
          if (bookedFuture) {
            return (
              <div className="cx-step">
                <h1 className="t1c-h1">You're booked</h1>
                <p className="mut t1c-lede">
                  Your interview for <b>{invite.requisitionTitle}</b> at <b>{invite.orgName}</b> is set for:
                </p>
                <div className="t1c-booked">
                  <span className="t1c-booked-ico" aria-hidden="true">
                    <Clock />
                  </span>
                  <div style={{ minWidth: 0 }}>
                    <div className="t1c-booked-when">
                      {timeLabel(invite.scheduledAt!)} · {dayLabel(invite.scheduledAt!)}
                    </div>
                    <div className="mut sm">In your timezone — {VIEWER_TZ.replace(/_/g, " ")}</div>
                  </div>
                </div>
                <p className="mut sm" style={{ margin: "0 0 12px" }}>
                  Come back to this link at that time. Want to go now? You'll need a quiet spot, camera and mic,
                  and about 30 minutes.
                </p>
                <div className="t1c-actions">
                  <button className="btn primary" onClick={() => setStartEarly(true)}>
                    Start now instead <Arrow />
                  </button>
                  <button className="btn ghost" onClick={() => setRescheduling(true)}>
                    <Calendar /> Reschedule
                  </button>
                </div>
                <SaveLinkCard token={token} savedEmail={invite.preferredEmail ?? null} />
              </div>
            );
          }
          return (
            <CheckinStep
              token={token}
              idvEnabled={invite.idvEnabled ?? false}
              idvMethod={invite.idvMethod ?? "id_document"}
              allowedIdTypes={invite.allowedIdTypes}
            />
          );
        })()}
        {invite && !expired && step === "done" && (
          <DoneStep
            token={token}
            orgName={invite.orgName}
            requisitionTitle={invite.requisitionTitle}
            candidateName={invite.candidateName}
            candidateEmail={invite.preferredEmail || invite.email}
            // Genuine outcome only — never keyed on idvEnabled, which would claim "Identity verified"
            // for a simulated/demo run. Defaults false until the API reports the real result.
            identityVerified={invite.identityVerified ?? false}
            retentionDays={invite.recordingRetentionDays}
          />
        )}
      </div>

      <p className="t1c-foot">
        You never see scores, rankings or a decision here — that's by design. Assessment goes to the employer;
        a human makes the call.
      </p>
      </div>
    </>
  );
}
